import React, { useEffect, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";

import useMe from "@/hooks/useMe";
import SearchIcon from "@/public/search-icon.svg";
import { encodeURL, mergeRefs } from "@/utils/index";

interface SearchBarProps
  extends Omit<React.HtmlHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string;
  position: "static" | "relative" | "absolute" | "fixed" | "sticky";
  onChange: (value: string) => void;
  onSubmit: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  defaultSingleRow?: string;
  hasRotation?: boolean;
  isLoading?: boolean;
  defaultPlaceholder?: string;
  onPlaceholderShow?: (idx: number) => void;
  onFocus?: () => void;
  onBlur?: () => void;
}

const wordingList = ["Help me find...", "What do I wear..."];

const waitFor = async (milliseconds: number) => {
  return new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });
};

const placeholderRotation = async (
  onChange: any,
  isFirst?: boolean,
  onPlaceholderShow?: (index: number) => void
) => {
  let i = 0;
  while (i >= 0) {
    const idx = i % wordingList.length;
    const wording = wordingList[idx];
    for (let j = 0; j <= wording.length; j++) {
      if (isFirst && i === 0) {
        j = wording.length;
      }
      const words = wording.substring(0, j);
      onChange(words);
      await waitFor(80);
    }
    if (onPlaceholderShow) onPlaceholderShow(idx);
    await waitFor(4000);
    for (let j = wording.length; j > 0; j--) {
      const words = wording.substring(0, j);
      onChange(words);
      await waitFor(80);
    }
    i++;
  }
};

const AutoGrowTextarea = (
  onChange: (value: string) => void,
  onSubmit: any,
  value: string,
  ref: any,
  defaultSingleRow?: string,
  placeholder?: string,
  isLoading?: boolean,
  onFocus?: () => void,
  onBlur?: () => void
) => {
  const [isMultiline, setIsMultiline] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [selected, setSelected] = useState(false);
  const [showClean, setShowClean] = useState(false);

  const { checkSearchCount } = useMe();

  const [startRotation, setStartRotation] = useState(true);

  const handleFocus = () => {
    setStartRotation(false);
    if (onFocus) onFocus();
  };

  const handleBlur = () => {
    setStartRotation(true);
    if (onBlur) onBlur();
  };

  useEffect(() => {
    if (!textareaRef || !textareaRef.current) return;

    if (textareaRef.current.scrollHeight > 24) {
      setIsMultiline(true);
    } else {
      setIsMultiline(false);
    }
  }, [value]);

  const handleChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { value: newValue } = event.target;
    onChange(newValue);

    // 更新高度
    if (defaultSingleRow == "true") {
      event.target.style.height = "24px";
    } else {
      event.target.style.height = "40px";
    }
    if (newValue) {
      event.target.style.height = event.target.scrollHeight + "px";
    }
  };

  const cleanInputValue = () => {
    if (textareaRef && textareaRef.current) {
      onChange("");
      const textarea = textareaRef.current;
      if (defaultSingleRow == "true") {
        textarea.style.height = "24px";
      } else {
        textarea.style.height = "40px";
      }
      textarea.focus();
    }
  };

  const moveCursorToEnd = () => {
    if (textareaRef && textareaRef.current) {
      const textarea = textareaRef.current;
      const len = textarea.value.length;
      textarea.setSelectionRange(len, len);
    }
  };

  // Set height to 24px when selected is false
  useEffect(() => {
    if (!textareaRef || !textareaRef.current) return;
    const searchTextareaRef = textareaRef.current;
    if (searchTextareaRef) {
      if (!selected) {
        if (defaultSingleRow == "true") {
          searchTextareaRef.style.height = "24px";
        } else {
          searchTextareaRef.style.height = "40px";
        }
        searchTextareaRef.scrollTop = 0;
      } else {
        // else make the height same as current text height
        searchTextareaRef.style.height = searchTextareaRef.scrollHeight + "px";
        searchTextareaRef.focus();
        searchTextareaRef.scrollTop = searchTextareaRef.scrollHeight;
        moveCursorToEnd();
      }
    }
  }, [selected, defaultSingleRow]);

  useEffect(() => {
    const handler = () => setSelected(false);
    const textarea = textareaRef?.current;
    if (textareaRef && textarea) {
      textarea.addEventListener("focusout", handler);
    }

    return () => {
      if (textareaRef && textarea) {
        textarea.removeEventListener("focusout", handler);
      }
    };
  }, [textareaRef]);

  useEffect(() => {
    setShowClean(selected);
  }, [selected]);

  return (
    <div className="search_textarea_over" onClick={() => setSelected(true)}>
      {/* Only for defaultSingleRow searchbar */}
      {defaultSingleRow === "true" && !selected && isMultiline && (
        <div className="blur"></div>
      )}
      <textarea
        // @ts-expect-error ignore ref type
        ref={mergeRefs(textareaRef, ref)}
        placeholder={startRotation && !value ? placeholder : ""}
        value={value}
        onChange={handleChange}
        className={`search_textarea ${
          defaultSingleRow === "true"
            ? `${selected ? "padding-right" : ""} single-row`
            : `${value ? "padding-right" : ""}`
        }  ${selected ? "selected" : ""}`}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault(); // 阻止默认的新行行为
            if (checkSearchCount(`/results/${encodeURL(value)}`)) {
              return;
            }
            if (onSubmit) {
              onSubmit(e);
            }
          }
        }}
        onFocus={handleFocus}
        onBlur={() => {
          setSelected(false);
          handleBlur();
        }}
        enterKeyHint="search"
      ></textarea>
      {(defaultSingleRow !== "true" || showClean) && !isLoading && (
        <img
          src="/icon-x.svg"
          onClick={cleanInputValue}
          className={`input-clean-icon ${value ? "show" : "hide"}`}
        ></img>
      )}
      {isLoading && (
        <div className="search-bar-spinner-mobile-wrapper right-4">
          <Spinner
            animation="border"
            size="sm"
            className="search-bar-spinner"
          />
        </div>
      )}
      <style jsx>{`
        .search_textarea_over {
          width: 100%;
          padding-top: 16px;
          padding-bottom: 16px;
          padding-right: 16px;
          padding-left: 48px;

          z-index: 1;
          box-shadow: 0px 16px 40px 0px rgba(0, 0, 0, 0.12);
          position: relative;
          background-color: #fff;
        }
        .search_textarea {
          width: 100%;
          height: 40px;
          max-height: 96px;
          min-height: 40px;
          display: block;
          padding-left: 12px;
          color: #191919;
          outline: none;
          background-color: #fff;
          resize: none;
          overflow-y: hidden;
          font-size: 14px;
          font-weight: 500;
          border-left: 1px solid #e5e5e5;
          overflow: auto;
          border-radius: 0;
        }
        .blur {
          position: absolute;
          width: 112px;
          height: 100%;
          background: linear-gradient(
            90deg,
            rgba(255, 255, 255, 0) 0%,
            #fff 67.86%
          );
          right: 0;
          top: 0;
        }
        .padding-right {
          padding-right: 32px;
        }
        .single-row {
          height: 24px;
          min-height: 24px;
        }
        .selected {
          min-height: 24px;
        }
        .input-clean-icon {
          position: absolute;
          top: 16px;
          right: 16px;
        }
        .hide {
          display: none;
        }

        .search-bar-spinner-mobile-wrapper {
          position: absolute;
          top: 16px;
          right: 16px;
          width: 16px;
          height: 16px;
        }
        @media (min-width: 768px) {
          .search_textarea_over {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

const SearchBar = React.forwardRef<HTMLInputElement, SearchBarProps>(
  (
    {
      value,
      position,
      onChange,
      onSubmit,
      defaultSingleRow,
      hasRotation = false,
      className,
      isLoading = false,
      defaultPlaceholder = 'Search things like "lightweight comfortable all white dress for a beach vacation',
      onPlaceholderShow,
      onFocus,
      onBlur,
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { checkSearchCount } = useMe();
    const [placeholder, setPlaceholder] = useState(
      hasRotation ? "" : defaultPlaceholder
    );
    const [startRotation, setStartRotation] = useState(true);

    const handleFocus = () => {
      setStartRotation(false);
      if (onFocus) {
        onFocus();
      }
    };

    const handleBlur = () => {
      setStartRotation(true);
      if (onBlur) {
        onBlur();
      }
    };

    useEffect(() => {
      if (hasRotation)
        placeholderRotation(setPlaceholder, false, onPlaceholderShow);
    }, [hasRotation]);

    return (
      <div style={{ position: position }} className="searchbar-wrapper">
        <div
          className={`${
            defaultSingleRow ? "singRow_input_over" : "mulRow_input_over"
          }`}
        >
          <input
            className={`search_input ${className}`}
            ref={mergeRefs(ref, inputRef)}
            style={{
              zIndex: 1,
              padding: "24px 48px",
              boxShadow: "0px 16px 40px 0px rgba(0, 0, 0, 0.12)",
              color: "#191919",
              outline: "none",
              backgroundColor: "#fff",
              fontSize: "16px",
              fontWeight: 500,
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                if (checkSearchCount(`/results/${encodeURL(value)}`)) {
                  return;
                }
                if (onSubmit) {
                  onSubmit(e);
                }
              }
            }}
            type="search"
            placeholder={startRotation && !value ? placeholder : ""}
            value={value}
            onChange={(e) => {
              onChange(e.target.value);
            }}
            enterKeyHint="search"
            onFocus={handleFocus}
            onBlur={handleBlur}
          />
          {AutoGrowTextarea(
            onChange,
            onSubmit,
            value,
            ref,
            defaultSingleRow,
            placeholder,
            isLoading,
            onFocus,
            onBlur
          )}
        </div>
        {value && !isLoading && (
          <img
            style={{ zIndex: 1, position: "absolute" }}
            src="/icon-x.svg"
            alt="Close Icon"
            onClick={() => {
              if (inputRef && inputRef.current) {
                const input = inputRef.current;
                onChange("");
                input.focus();
              }
            }}
            className={`absolute top-1/2 right-4 transform -translate-y-1/2 cursor-pointer p-2 clean_icon_web`}
          />
        )}
        {isLoading && (
          <div className="search-bar-spinner-wrapper right-4 mr-2">
            <Spinner
              animation="border"
              size="sm"
              className="search-bar-spinner"
            />
          </div>
        )}
        {/* <img
          style={{ zIndex: 1, position: "absolute" }}
          src="/search-icon.svg"
          alt="Search Icon"
          className={`absolute top-1/2 left-4 transform -translate-y-1/2 search_icon_mobile`}
        /> */}
        <SearchIcon
          style={{ zIndex: 1, position: "absolute" }}
          className={`absolute top-1/2 left-4 transform -translate-y-1/2 search_icon_mobile`}
        />

        <style jsx>{`
          .searchbar-wrapper {
            z-index: 1;
          }
          .hide {
            display: none;
          }
          .search_input {
            width: 100%;
            height: 72px;
          }
          @media (max-width: 767px) {
            .clean_icon_web {
              display: none;
            }
            .search_input {
              display: none;
            }
            .search_icon_mobile {
              top: 28px;
            }
            .input-over-Gradient {
              display: block;
              position: absolute;
              top: 0;
              right: 0px;
              z-index: 4;
            }
            .search_input2 {
              padding-right: 0 !important;
            }
            .singRow_input_over {
              height: 56px;
            }
            .mulRow_input_over {
              height: 72px;
            }
          }
        `}</style>

        <style jsx global>{`
          .search-bar-spinner-wrapper {
            position: absolute;
            width: 18px;
            height: 18px;
            top: 50%;
            transform: translateY(-50%);
          }
          .search-bar-spinner {
            width: 100%;
            height: 100%;
            border-width: 2.5px;
          }
          .search_icon_mobile {
            @media (max-width: 767px) {
              top: 28px;
            }
          }
        `}</style>
      </div>
    );
  }
);

SearchBar.displayName = "SearchBar";
export default SearchBar;
